import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, inject, input, output } from '@angular/core';
import { ButtonIconComponent, ButtonDisplayStyle, ButtonSize } from '@workbuddy/ui/v2/components/button';
import { BadgeComponent, BadgeMode } from '@workbuddy/ui/components/badge';
import { AgentPanelService } from '../../services/agent.panel.service';
import { AgentChatContainerComponent } from '../../components/chat.container/chat.container.component';

@Component({
  selector: 'agent-popup-chat',
  standalone: true,
  imports: [AgentChatContainerComponent, ButtonIconComponent, BadgeComponent],
  templateUrl: './popup.chat.component.html',
  styleUrls: ['./popup.chat.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class AgentPopupChatComponent {

  protected readonly ButtonDisplayStyle = ButtonDisplayStyle;
  protected readonly ButtonSize = ButtonSize;
  protected readonly BadgeMode = BadgeMode;

  protected readonly panelService = inject(AgentPanelService);

  protected readonly $headerTitle = computed(() => {

    const activeId = this.panelService.$activeTabId();
    const tabs = this.panelService.$openTabs();
    const tab = tabs.find(t => t.threadId === activeId);
    return tab?.title ?? 'New chat';
  });

  public readonly close = output<void>();

  // Whether the window has been dragged/resized off its default spot — the shell owns
  // the geometry, so it tells the header when to reveal the "reset position" button.
  public readonly $canReset = input(false, { alias: 'canReset' });

  // True while the shell is actively dragging/resizing the window. Drives the reset-hint
  // overlay directly: it is live for exactly as long as the gesture lasts, so there is no
  // timer to manage and no chance of it lingering after the pointer is released.
  public readonly $dragging = input(false, { alias: 'dragging' });

  // Emitted when a drag begins on the title bar (not on the window-control buttons),
  // so the shell — which owns the positioned panel wrapper — can move the window.
  public readonly dragStart = output<PointerEvent>();

  // Asks the shell to snap the window back to its original position + size.
  public readonly resetPosition = output<void>();

  protected onHeaderDoubleClick(event: MouseEvent): void {

    // Double-clicking the title bar snaps the window home (mirrors the OS window
    // convention) — but never when the double-click lands on the control buttons.
    if ((event.target as HTMLElement).closest('.header-actions')) return;
    this.resetPosition.emit();
  }

  protected onHeaderPointerDown(event: PointerEvent): void {

    // Left button only, and never start a drag from the minimize/expand/close cluster.
    if (event.button !== 0) return;
    if ((event.target as HTMLElement).closest('.header-actions')) return;
    this.dragStart.emit(event);
  }

  protected onMinimize(): void {

    this.panelService.minimize();
  }

  protected onExpand(): void {

    this.panelService.setDisplayMode('pseudo');
  }

  protected onCollapse(): void {

    this.panelService.setDisplayMode('popup');
  }

  protected onClose(): void {

    const threadId = this.panelService.$activeThreadId();
    if (threadId) {
      this.panelService.closeTab(threadId);
    }
    this.panelService.closePanel();
    this.close.emit();
  }

}
